import React from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { gql } from '@soundxyz/gql-string';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type { FragmentType } from '@graphql/reactQuery';
import {
  CollectorCardInfoFragmentDoc,
  getFragment,
  UserForCollectorFragmentDoc,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import type { CollectorsSort } from '..';
import { Card, InfoContainer, LeftContainer, Name, Rank, RightContainer, Top } from './styles';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorCardInfo on ArtistCollector {
    id
    nftsCount
    firstNftCollectedDate
    firstNftCollected {
      id
      release {
        id
        title
      }
    }
    artist {
      id
      name
      webappUri
      user {
        ...UserForCollector
      }
    }
    user {
      ...UserForCollector
    }
  }

  fragment UserForCollector on User {
    id
    publicAddress
    avatar {
      id
      url
    }
    artist {
      id
    }
    username
    webappUri
  }
`);

const AVATAR_SIZE = 56;

export type SharedCardProps = {
  variant: 'collector' | 'artistBacked';
  seeAllPage: boolean;
};

export const CardInfo = ({
  collector,
  index,
  variant,
  seeAllPage = false,
  sort,
}: {
  collector: FragmentType<CollectorCardInfoFragmentDoc>;
  index: number;
  sort: CollectorsSort;
} & SharedCardProps) => {
  const collectorData = getFragment(CollectorCardInfoFragmentDoc, collector);
  const user = {
    ...collectorData.user,
    ...getFragment(UserForCollectorFragmentDoc, collectorData.user),
  };
  const artist = {
    ...collectorData.artist,
    ...getFragment(UserForCollectorFragmentDoc, collectorData.artist.user),
  };
  const soundsCollected = collectorData?.nftsCount;
  const isArtistBacked = variant === 'artistBacked';

  const name = isArtistBacked ? artist.name : user.username;
  const avatarUrl = isArtistBacked ? artist.avatar?.url || '' : user.avatar?.url || '';

  const releaseName = collectorData.firstNftCollected.release.title;
  const collectedDate = collectorData.firstNftCollectedDate;

  const hasArtistRole = isArtistBacked && artist ? true : !!user?.artist;
  const walletAddress = isArtistBacked ? artist.publicAddress : user.publicAddress;
  const username = isArtistBacked ? artist.username : user.username;
  const webappUri = isArtistBacked ? artist.webappUri : user.webappUri;

  const SUBTEXT = sort === 'earliest' ? `${releaseName ?? '...'}` : `${soundsCollected} Collected`;
  const avatar = (
    <div
      className={clsx(
        'relative flex h-14 w-14 items-center overflow-hidden rounded-bl-md',
        !!avatarUrl ? 'bg-white' : 'bg-black',
      )}
    >
      <Avatar
        size={AVATAR_SIZE}
        src={avatarUrl}
        walletAddress={walletAddress}
        username={username}
        borderRadius="0 0 0 8px"
        disableLink
        priority
        webappUri={webappUri}
      />
    </div>
  );

  const info = (
    <div className="w-full">
      <span
        className={clsx(
          'overflow-hidden text-ellipsis whitespace-nowrap leading-4 ',
          sort === 'earliest' && 'max-w-[250px] lg:max-w-[115px] xl:max-w-[160px]',
        )}
      >
        {SUBTEXT}
      </span>
      <span className="overflow-hidden text-ellipsis whitespace-nowrap leading-4">
        Since {format(new Date(collectedDate), 'MMM yyyy')}
      </span>
    </div>
  );

  return (
    <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
      <Card expandedSection={seeAllPage}>
        <LeftContainer>
          <Top borderRight>
            <Rank>{index}</Rank>
          </Top>
          {avatar}
        </LeftContainer>
        <RightContainer expandedSection={seeAllPage}>
          <Top>
            <div className="flex overflow-hidden lg:w-fit lg:max-w-full">
              <Name seeAllPage={seeAllPage}>{name}</Name>
            </div>
            <div className="ml-1 flex items-center gap-1">
              {hasArtistRole && <VerifiedTooltip toolTipOffset={0} portal />}
            </div>
          </Top>

          <InfoContainer>{info}</InfoContainer>
        </RightContainer>
      </Card>
    </AccessibleLink>
  );
};
